import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CosmicNode, LINKS, ResourceType } from '../data/cosmicNodes';

const CAT_LABEL: Record<string, string> = {
  celestial: 'Celestial Being',
  creature:  'Entity / Cryptid',
  place:     'Sacred Site',
  concept:   'Doctrine & Concept',
  palingen:  'Palingen Studios',
};

const CAT_COLOR: Record<string, string> = {
  celestial: '#c9a227',
  creature:  '#bf3b30',
  place:     '#22a882',
  concept:   '#7a56a8',
  palingen:  '#d8c890',
};

interface NodeCardProps {
  node:     CosmicNode | null;
  allNodes: CosmicNode[];
  onClose:  () => void;
}

const RESOURCE_ICON: Record<ResourceType, string> = {
  book:    '📖',
  site:    '🔗',
  video:   '▶',
  article: '📄',
};

const NodeCard: React.FC<NodeCardProps> = ({ node, allNodes, onClose }) => {
  const color = node ? (CAT_COLOR[node.category] ?? '#d8c890') : '#d8c890';
  const [imgExpanded, setImgExpanded] = useState(false);
  const [resOpen,     setResOpen]     = useState(false);
  React.useEffect(() => { setResOpen(false); setImgExpanded(false); }, [node?.id]);

  const connected: CosmicNode[] = node
    ? LINKS
        .filter(l => l.source === node.id || l.target === node.id)
        .map(l  => (l.source === node.id ? l.target : l.source))
        .map(id => allNodes.find(n => n.id === id))
        .filter((n): n is CosmicNode => Boolean(n))
    : [];

  return (
    <AnimatePresence>
      {node && (
        <motion.div
          key={node.id}
          initial={{ x: '100%', opacity: 0 }}
          animate={{ x: 0,      opacity: 1 }}
          exit={{    x: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 30, stiffness: 320 }}
          style={{
            position:       'fixed',
            top:            0,
            right:          0,
            width:          'min(430px, 92vw)',
            height:         '100vh',
            background:     'rgba(5, 6, 20, 0.97)',
            borderLeft:     `1px solid ${color}28`,
            backdropFilter: 'blur(14px)',
            padding:        '36px 28px 40px',
            overflowY:      'auto',
            zIndex:         200,
            boxShadow:      `-6px 0 70px rgba(0,0,0,0.85)`,
          }}
        >
          {/* Close */}
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position:        'absolute',
              top:             20,
              right:           20,
              width:           32,
              height:          32,
              borderRadius:    '50%',
              background:      'transparent',
              border:          '1px solid rgba(255,255,255,0.18)',
              color:           'rgba(255,255,255,0.6)',
              cursor:          'pointer',
              fontSize:        15,
              display:         'flex',
              alignItems:      'center',
              justifyContent:  'center',
              lineHeight:      1,
              transition:      'border-color 0.2s, color 0.2s',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = color;
              (e.currentTarget as HTMLButtonElement).style.color       = color;
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.18)';
              (e.currentTarget as HTMLButtonElement).style.color       = 'rgba(255,255,255,0.6)';
            }}
          >
            ✕
          </button>

          {/* Category badge */}
          <div style={{
            display:       'inline-block',
            padding:       '3px 11px',
            borderRadius:  12,
            background:    `${color}18`,
            border:        `1px solid ${color}55`,
            color:         color,
            fontSize:      10,
            fontFamily:    'Georgia, serif',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom:  18,
          }}>
            {CAT_LABEL[node.category] ?? node.category}
          </div>

          {/* Title */}
          <h2 style={{
            margin:     '0 0 18px',
            color:      '#f0e8d0',
            fontFamily: "'Palatino Linotype','Book Antiqua',Georgia,serif",
            fontSize:   'clamp(20px, 4vw, 26px)',
            fontWeight: 600,
            lineHeight: 1.2,
          }}>
            {node.label}
          </h2>

          {/* Node image */}
          {node.image && (
            <>
              <div
                title="Hover to expand"
                onMouseEnter={() => setImgExpanded(true)}
                style={{
                  margin:       '0 0 20px',
                  borderRadius: 8,
                  overflow:     'hidden',
                  border:       `1px solid ${color}33`,
                  boxShadow:    `0 4px 24px rgba(0,0,0,0.5)`,
                  cursor:       'zoom-in',
                  position:     'relative',
                }}
              >
                <img
                  src={node.image}
                  alt={node.label}
                  style={{
                    display:    'block',
                    width:      '100%',
                    maxHeight:  220,
                    objectFit:  'cover',
                    transition: 'opacity 0.2s',
                  }}
                />
                <div style={{
                  position:   'absolute',
                  bottom:     8,
                  right:      10,
                  fontSize:   10,
                  color:      'rgba(255,255,255,0.45)',
                  fontFamily: 'Georgia, serif',
                  letterSpacing: '0.06em',
                  pointerEvents: 'none',
                }}>
                  hover to expand
                </div>
              </div>

              {/* Lightbox overlay — portalled to body to escape backdrop-filter stacking context */}
              {createPortal(
                <AnimatePresence>
                  {imgExpanded && (
                    <motion.div
                      key="lightbox"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{    opacity: 0 }}
                      transition={{ duration: 0.18 }}
                      onMouseLeave={() => setImgExpanded(false)}
                      onClick={()      => setImgExpanded(false)}
                      style={{
                        position:       'fixed',
                        inset:          0,
                        zIndex:         9999,
                        background:     'rgba(0,0,0,0.88)',
                        display:        'flex',
                        alignItems:     'center',
                        justifyContent: 'center',
                        cursor:         'zoom-out',
                        backdropFilter: 'blur(6px)',
                      }}
                    >
                      <motion.img
                        src={node.image}
                        alt={node.label}
                        initial={{ scale: 0.88, opacity: 0 }}
                        animate={{ scale: 1,    opacity: 1 }}
                        exit={{    scale: 0.88, opacity: 0 }}
                        transition={{ type: 'spring', damping: 28, stiffness: 280 }}
                        style={{
                          maxWidth:     '92vw',
                          maxHeight:    '92vh',
                          objectFit:    'contain',
                          borderRadius: 6,
                          boxShadow:    `0 0 80px rgba(0,0,0,0.9), 0 0 0 1px ${color}44`,
                        }}
                        onClick={e => e.stopPropagation()}
                      />
                      <div style={{
                        position:      'absolute',
                        bottom:        24,
                        left:          '50%',
                        transform:     'translateX(-50%)',
                        color:         'rgba(255,255,255,0.35)',
                        fontSize:      11,
                        fontFamily:    'Georgia, serif',
                        letterSpacing: '0.08em',
                        pointerEvents: 'none',
                        whiteSpace:    'nowrap',
                      }}>
                        {node.label} — move mouse away or click to close
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>,
                document.body
              )}
            </>
          )}

          {/* Ruled divider */}
          <div style={{
            height:       1,
            background:   `linear-gradient(90deg, ${color}55, transparent)`,
            marginBottom: 20,
          }} />

          {/* Scripture pull-quote */}
          {node.scripture && (
            <div style={{
              margin:       '0 0 22px',
              padding:      '12px 16px',
              borderLeft:   `3px solid ${color}88`,
              background:   `${color}0a`,
              borderRadius: '0 6px 6px 0',
            }}>
              <p style={{
                margin:     0,
                color:      `${color}cc`,
                fontFamily: "'Palatino Linotype','Book Antiqua',Georgia,serif",
                fontSize:   13,
                lineHeight: 1.65,
                fontStyle:  'italic',
              }}>
                {node.scripture}
              </p>
            </div>
          )}

          {/* Description */}
          <p style={{
            margin:     '0 0 30px',
            color:      'rgba(208,192,158,0.90)',
            fontFamily: "'Palatino Linotype','Book Antiqua',Georgia,serif",
            fontSize:   15,
            lineHeight: 1.75,
          }}>
            {node.description}
          </p>

          {/* Connected nodes */}
          {connected.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <p style={{
                margin:        '0 0 12px',
                color:         'rgba(170,148,90,0.65)',
                fontSize:      10,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                fontFamily:    'Georgia,serif',
              }}>
                Connected
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {connected.map(cn => (
                  <span key={cn.id} style={{
                    padding:      '4px 11px',
                    borderRadius: 10,
                    background:   'rgba(255,255,255,0.05)',
                    border:       '1px solid rgba(255,255,255,0.11)',
                    color:        'rgba(200,183,140,0.82)',
                    fontSize:     11,
                    fontFamily:   'Georgia,serif',
                  }}>
                    {cn.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Resources accordion */}
          {node.resources && node.resources.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <button
                onClick={() => setResOpen(o => !o)}
                style={{
                  display:        'flex',
                  alignItems:     'center',
                  gap:            8,
                  width:          '100%',
                  background:     `${color}0d`,
                  border:         `1px solid ${color}33`,
                  borderRadius:   resOpen ? '7px 7px 0 0' : 7,
                  padding:        '9px 14px',
                  cursor:         'pointer',
                  color:          color,
                  fontFamily:     'Georgia, serif',
                  fontSize:       11,
                  letterSpacing:  '0.1em',
                  textTransform:  'uppercase',
                  transition:     'background 0.2s',
                }}
                onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.background = `${color}1a`)}
                onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = `${color}0d`)}
              >
                <span style={{ flex: 1, textAlign: 'left' }}>Resources & Further Study</span>
                <span style={{
                  fontSize:   13,
                  transition: 'transform 0.25s',
                  display:    'inline-block',
                  transform:  resOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                }}>▾</span>
              </button>

              <AnimatePresence initial={false}>
                {resOpen && (
                  <motion.div
                    key="res"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{    height: 0, opacity: 0 }}
                    transition={{ duration: 0.22, ease: 'easeInOut' }}
                    style={{ overflow: 'hidden' }}
                  >
                    <div style={{
                      border:       `1px solid ${color}33`,
                      borderTop:    'none',
                      borderRadius: '0 0 7px 7px',
                      padding:      '4px 0',
                      background:   'rgba(0,0,0,0.25)',
                    }}>
                      {node.resources!.map((r, i) => (
                        <a
                          key={i}
                          href={r.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display:        'flex',
                            alignItems:     'flex-start',
                            gap:            10,
                            padding:        '9px 14px',
                            textDecoration: 'none',
                            borderBottom:   i < node.resources!.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                            transition:     'background 0.15s',
                          }}
                          onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.background = `${color}12`)}
                          onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.background = 'transparent')}
                        >
                          <span style={{ fontSize: 14, marginTop: 1, opacity: 0.75, flexShrink: 0 }}>
                            {RESOURCE_ICON[r.type]}
                          </span>
                          <span style={{ flex: 1 }}>
                            <span style={{
                              display:    'block',
                              color:      'rgba(220,200,155,0.9)',
                              fontFamily: "'Palatino Linotype','Book Antiqua',Georgia,serif",
                              fontSize:   13,
                              lineHeight: 1.4,
                            }}>
                              {r.label}
                            </span>
                            {r.author && (
                              <span style={{
                                display:    'block',
                                color:      'rgba(170,148,90,0.6)',
                                fontFamily: 'Georgia, serif',
                                fontSize:   11,
                                marginTop:  2,
                              }}>
                                {r.author}
                              </span>
                            )}
                          </span>
                          <span style={{
                            fontSize:  9,
                            color:     `${color}77`,
                            flexShrink: 0,
                            marginTop:  3,
                            fontFamily: 'Georgia, serif',
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                          }}>
                            {r.type}
                          </span>
                        </a>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* CTA — Commission node */}
          {node.id === 'contact' && (
            <a
              href="mailto:palingenstudios@gmail.com"
              style={{
                display:        'block',
                marginTop:      8,
                padding:        '13px 20px',
                background:     `${color}14`,
                border:         `1px solid ${color}44`,
                color:          color,
                borderRadius:   8,
                textAlign:      'center',
                textDecoration: 'none',
                fontFamily:     'Georgia,serif',
                fontSize:       13,
                letterSpacing:  '0.07em',
                transition:     'background 0.2s',
              }}
              onMouseEnter={e =>
                ((e.currentTarget as HTMLAnchorElement).style.background = `${color}26`)
              }
              onMouseLeave={e =>
                ((e.currentTarget as HTMLAnchorElement).style.background = `${color}14`)
              }
            >
              Get in Touch
            </a>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NodeCard;
